import app from 'flarum/forum/app';
import { confirmAsync } from '../../common/components/confirmAsync';
import Page from 'flarum/common/components/Page';
import { MpNavHelper } from './MpNavHelper';
import MpSkeleton from './MpSkeleton';
import Button from 'flarum/common/components/Button';
import { api } from '../../common/utils/api';
import { apiPreload } from '../../common/utils/preload';
import { composerRequire, copyCommand } from '../../common/utils/clipboard';
import { formatMoney } from '../../common/utils/currency';
import { cartStore } from '../../common/utils/cartStore';
import { cartAddFeedback } from '../../common/utils/cartAddFeedback';
import WishlistButton from './WishlistButton';
import ReportProductModal from './ReportProductModal';
import LogInModal from 'flarum/forum/components/LogInModal';
import { mpImage } from '../../common/utils/mpImage';
export default class ProductPage extends Page {
  product = null;
  reviews = [];
  reviewMeta = {
    average: 0,
    total: 0
  };
  loading = true;
  qty = 1;
  myRating = 5;
  myComment = '';
  submittingReview = false;
  tab = 'overview';
  readmeHtml = null;
  readmeLoading = false;
  readmeLoaded = false;
  clVersions = [];
  clTotal = 0;
  clOffset = 0;
  clLimit = 2;
  clLoading = false;
  clLoaded = false;
  lightboxIndex = null;
  // Estado pulse após "Adicionar ao carrinho": muda label/ícone por 2s,
  // então dá lugar ao estado persistente "Já no carrinho" (isInCart) —
  // evita que o cliente clique de novo e duplique o item sem perceber.
  justAddedToCart = false;
  justAddedTimer = null;
  reactivating = false;
  // Ciclo escolhido pelo cliente (mensal × anual). Inicializado em load()
  // baseado no billing_cycle do produto. Só usado quando has_dual_billing.
  selectedCycle = 'monthly';
  selectedLicense = 'regular';
  // Recomendações ("Você também pode gostar"): co-compra + mesma categoria.
  related = [];
  relatedLoaded = false;
  oninit(vnode) {
    super.oninit(vnode);
    this.bodyClass = 'MarketplacePage App--index MpProduct-body';
    app.mpProductType = null;
    this.load();
  }
  onremove(vnode) {
    super.onremove(vnode);
    document.body.style.overflow = '';
    app.mpProductType = null;
    if (this.justAddedTimer) clearTimeout(this.justAddedTimer);
  }
  async reactivateSubscription(p) {
    if (this.reactivating || !p.existing_subscription_id) return;
    const ok = await confirmAsync({
      title: this.t('product.reactivate_title') || 'Reativar assinatura',
      message: this.t('product.confirm_reactivate') || 'Reativar a assinatura? Ela vai voltar a renovar normalmente.',
      confirmLabel: this.t('product.reactivate_action') || 'Reativar'
    });
    if (!ok) return;
    this.reactivating = true;
    m.redraw();
    try {
      await api('POST', `/marketplace/my/subscriptions/${p.existing_subscription_id}/reactivate`);
      p.subscription_cancelling = false;
      app.alerts.show({
        type: 'success'
      }, this.t('product.reactivated') || 'Assinatura reativada');
    } catch (e) {
      const code = e?.response?.error ?? 'reactivate_error';
      app.alerts.show({
        type: 'error'
      }, this.t('product.' + code) || this.t('product.reactivate_error') || 'Não foi possível reativar');
    } finally {
      this.reactivating = false;
      m.redraw();
    }
  }
  reportProduct(productId) {
    if (!app.session.user) {
      app.modal.show(LogInModal);
      return;
    }
    app.modal.show(ReportProductModal, {
      productId
    });
  }
  async addToCart(productId) {
    if (this.justAddedTimer) clearTimeout(this.justAddedTimer);
    // Feedback otimista: dispara NO CLIQUE, antes do round-trip de rede —
    // senão a "batida" verde do botão atrasa uns cento e poucos ms e o
    // clique parece não ter sido captado. Erro reverte abaixo.
    cartAddFeedback.success(null);
    this.justAddedToCart = true;
    m.redraw();
    // 2s: tempo suficiente pro cliente perceber a confirmação visual
    // antes do botão trocar pro estado persistente "Já no carrinho"
    // (isInCart). Aplica-se a todos os tipos (one-time, assinaturas,
    // serviços) — feedback consistente.
    this.justAddedTimer = setTimeout(() => {
      this.justAddedToCart = false;
      this.justAddedTimer = null;
      m.redraw();
    }, 2000);
    try {
      // Só passa cycle se o produto suporta dual-billing — caso contrário
      // o backend usa billing_cycle do produto como default.
      const cycle = this.product?.has_dual_billing ? this.selectedCycle : undefined;
      const licenseType = this.product?.has_extended_license && this.selectedLicense === 'extended' ? 'extended' : undefined;
      await cartStore.add(productId, this.qty, cycle, licenseType);
    } catch (e) {
      // Reverte o feedback otimista — a request falhou, o item não foi
      // pro carrinho de verdade.
      if (this.justAddedTimer) {
        clearTimeout(this.justAddedTimer);
        this.justAddedTimer = null;
      }
      this.justAddedToCart = false;
      m.redraw();
      const code = e?.response?.error ?? '';
      // Backend rejeita compra duplicada com error="already_owned" (ou
      // "bundle_already_owns:Nome" pra bundles). Recarregamos o produto
      // pra atualizar `already_subscribed` / `already_owned` e o botão
      // já alterna pro estado correto sozinho.
      if (code === 'already_owned' || code.startsWith?.('bundle_already_owns')) {
        app.alerts.show({
          type: 'warning'
        }, this.t('product.already_owned_alert') || 'Você já possui este produto.');
        this.load();
        return;
      }
      app.alerts.show({
        type: 'error'
      }, this.t('shop.add_error') || 'Erro ao adicionar');
    }
  }

  /** Produto já tem item correspondente no carrinho (persiste entre navegações). */
  isInCart(productId) {
    return cartStore.get().items.some(i => i.product_id === productId);
  }

  /** Ciclo efetivo: dual-billing usa selectedCycle, senão cai no billing_cycle. */
  effectiveCycle(p) {
    if (p.has_dual_billing) return this.selectedCycle;
    return p.billing_cycle || 'one_time';
  }

  /** Preço a exibir conforme o ciclo efetivo + tier de licença. */
  activePriceFor(p) {
    if (p.has_extended_license && this.selectedLicense === 'extended') {
      return Number(p.extended_price || p.price);
    }
    if (p.has_dual_billing && this.selectedCycle === 'yearly') {
      return Number(p.price_yearly || p.price);
    }
    if (p.on_sale && p.sale_price != null) {
      return Number(p.sale_price);
    }
    return Number(p.price);
  }

  /** Preço "de" (riscado) quando há promoção ativa no tier regular. */
  strikePriceFor(p) {
    if (p.on_sale && p.sale_price != null && this.selectedLicense !== 'extended' && !(p.has_dual_billing && this.selectedCycle === 'yearly')) {
      return Number(p.price);
    }
    return null;
  }
  async load() {
    this.loading = true;
    m.redraw();
    const vendor = m.route.param('vendor');
    const name = m.route.param('name');
    const slug = m.route.param('slug');
    const id = m.route.param('id');
    let endpoint;
    if (vendor && name) endpoint = `/marketplace/products/${vendor}/${name}`;else if (slug) endpoint = `/marketplace/items/${slug}`;else endpoint = `/marketplace/products/${id}`;
    try {
      const r = await apiPreload('product', endpoint);
      this.product = r.data;
      app.mpProductType = this.product?.type ?? null;

      // Default do seletor de ciclo: o billing_cycle padrão do produto
      // (se o admin marcou "yearly", começa em yearly; senão monthly).
      if (this.product?.has_dual_billing) {
        this.selectedCycle = this.product.billing_cycle === 'yearly' ? 'yearly' : 'monthly';
      }

      // Atualiza título com nome do produto (SEO + UX da aba do navegador).
      if (this.product?.name) {
        app.setTitle(this.product.name);
      }

      // Sempre normaliza para URL canônica (pretty URL) quando chegou
      // via /product/:id ou quando a identificação pode ser melhorada.
      const canonical = this.canonicalUrl(this.product);
      const current = m.route.get();
      if (canonical && current && !current.startsWith(canonical)) {
        m.route.set(canonical, null, {
          replace: true
        });
        return;
      }
      if (this.product?.id) {
        const rv = await apiPreload('product_reviews', `/marketplace/products/${this.product.id}/reviews`);
        this.reviews = rv.data ?? [];
        this.reviewMeta = rv.meta ?? this.reviewMeta;
        // Recomendações: best-effort, não bloqueia o render do produto.
        this.loadRelated(this.product.id);
      }
    } catch (e) {
      app.alerts.show({
        type: 'error'
      }, this.t('product.load_error'));
    } finally {
      this.loading = false;
      m.redraw();
    }
  }

  // URL canônica: /extension/{vendor}/{name} se tem package_name,
  // /item/{slug} se tem slug, senão /product/{id} como último recurso.
  canonicalUrl(p) {
    if (!p) return null;
    if (p.package_name && p.package_name.includes('/')) {
      const [v, n] = p.package_name.split('/');
      return app.route('marketplace.extension', {
        vendor: v,
        name: n
      });
    }
    if (p.slug) {
      return app.route('marketplace.item', {
        slug: p.slug
      });
    }
    return null;
  }
  t(k, vars) {
    return app.translator.trans('ramon-marketplace.forum.' + k, vars);
  }

  // Cor derivada do nome do produto
  colorFor(name) {
    const palette = ['#3f88f6', '#8b5cf6', '#e7672e', '#22c55e', '#f59e0b', '#ef4444', '#06b6d4', '#ec4899'];
    let h = 0;
    for (let i = 0; i < name.length; i++) h = h * 31 + name.charCodeAt(i) >>> 0;
    return palette[h % palette.length];
  }
  iconFor(p) {
    if (p.package_name) return 'fab fa-php';
    const slug = p.category?.slug ?? '';
    if (slug.includes('theme')) return 'fas fa-palette';
    if (slug.includes('language')) return 'fas fa-language';
    if (p.type === 'digital') return 'fas fa-cloud-download-alt';
    return 'fas fa-box';
  }
  formatDate(iso) {
    if (!iso) return '';
    return new Date(iso).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      timeZone: 'America/Sao_Paulo'
    });
  }
  billingLabel(p) {
    if (p.is_open_source) return this.t('shop.open_source');
    // Em produtos com dual-billing o label segue o ciclo escolhido pelo
    // cliente — caso contrário mantém o billing_cycle do produto.
    const cycle = p.has_dual_billing ? this.selectedCycle : p.billing_cycle;
    if (cycle === 'monthly') return this.t('shop.billing_monthly');
    if (cycle === 'yearly') return this.t('shop.billing_yearly');
    return this.t('shop.billing_one_time');
  }
  view() {
    if (this.loading) {
      return m("div", {
        className: "MpProduct"
      }, MpNavHelper(), m(MpSkeleton, {
        variant: "product",
        composer: !!m.route.param('vendor')
      }));
    }
    if (!this.product) {
      return m("div", {
        className: "MpProduct"
      }, MpNavHelper(), m("div", {
        className: "MpProduct-notFound"
      }, m("i", {
        className: "fas fa-exclamation-circle"
      }), m("p", null, this.t('product.not_found') || 'Produto não encontrado')));
    }
    const p = this.product;
    if (p.type === 'course') return this.renderCourseView(p);
    const color = this.colorFor(p.name);
    const isFree = !!p.is_open_source;
    const isRecurring = p.billing_cycle === 'monthly' || p.billing_cycle === 'yearly';
    const isPool = !!p.unique_per_buyer;
    const poolAvail = Number(p.pool_available ?? 0);
    const outOfStock = p.type === 'physical' && p.stock <= 0 || isPool && poolAvail <= 0;
    const isComposer = !!p.package_name;
    return m("div", {
      className: "MpProduct"
    }, MpNavHelper(), m("a", {
      href: app.route('marketplace.shop'),
      config: m.route.link,
      className: "MpProduct-back"
    }, m("i", {
      className: "fas fa-chevron-left"
    }), " ", this.t('product.back_to_store') || 'Voltar à loja'), m("section", {
      className: "MpProduct-hero"
    }, m("div", {
      className: "MpProduct-heroLeft"
    }, m("div", {
      className: 'MpProduct-heroIcon' + (p.is_book ? ' MpProduct-heroIcon--book' : ''),
      style: {
        '--mp-icon-bg': color + '1f',
        '--mp-icon-fg': color
      }
    }, p.image ? m("img", Object.assign({}, mpImage(p.image), {
      alt: p.name
    })) : m("i", {
      className: this.iconFor(p)
    })), m("div", {
      className: "MpProduct-heroMeta"
    }, m("h1", {
      className: "MpProduct-title"
    }, p.name), p.seller && p.seller.username && m("a", {
      href: app.route('marketplace.user_seller', {
        username: p.seller.username
      }),
      config: m.route.link,
      className: "MpProduct-seller",
      title: this.t('product.by_seller', {
        name: p.seller.store_name
      })
    }, m("i", {
      className: "fas fa-store"
    }), " ", p.seller.store_name, p.seller.rating > 0 && m("span", {
      className: "MpProduct-seller-rating"
    }, m("i", {
      className: "fas fa-star"
    }), " ", Number(p.seller.rating).toFixed(1), " (", p.seller.rating_count, ")")), m("div", {
      className: "MpProduct-heroRow"
    }, p.category && m("span", {
      className: "MpProduct-author"
    }, m("i", {
      className: "fas fa-tag"
    }), " ", p.category.name), p.category && m("div", {
      className: "MpProduct-dot"
    }), m("div", {
      className: "MpProduct-heroBadges"
    }, isFree && m("span", {
      className: "MpChip MpChip--sub"
    }, m("i", {
      className: "fas fa-code"
    }), " ", this.t('shop.open_source') || 'Open source'), isRecurring && m("span", {
      className: "MpChip MpChip--sub"
    }, m("i", {
      className: "fas fa-sync-alt"
    }), this.effectiveCycle(p) === 'yearly' ? this.t('shop.billing_yearly') : this.t('shop.billing_monthly')), isComposer && m("span", {
      className: "MpChip MpChip--ot"
    }, m("i", {
      className: "fab fa-php"
    }), " Composer"), p.visibility === 'private' && m("span", {
      className: "MpChip MpChip--private",
      "data-tooltip": this.t('shop.private_hint') || 'Acesso restrito por convite'
    }, m("i", {
      className: "fas fa-lock"
    }), this.t('shop.private_label') || 'Privada'), p.unique_per_buyer && m("span", {
      className: "MpChip MpChip--pool",
      "data-tooltip": this.t('shop.pool_hint') || 'Cada compra entrega 1 arquivo único do pool'
    }, m("i", {
      className: "fas fa-ticket-alt"
    }), (p.pool_available ?? 0) > 0 ? this.t('shop.pool_left', {
      count: p.pool_available
    }) || `${p.pool_available} restantes` : this.t('shop.pool_sold_out') || 'Esgotado'), p.rating > 0 && m("span", {
      className: "MpProduct-rating"
    }, this.renderStars(p.rating), m("strong", null, Number(p.rating).toFixed(1)), p.reviews_count ? m("small", null, "(", p.reviews_count, ")") : null))))), m("div", {
      className: "MpProduct-heroRight"
    }, m("div", {
      className: "MpProduct-priceBlock"
    }, isFree ? m("div", {
      className: "MpProduct-priceFree"
    }, this.t('shop.free') || 'Grátis') : m('[', null, m("div", {
      className: "MpProduct-priceAmount"
    }, this.strikePriceFor(p) != null && m("s", {
      className: "MpProduct-priceWas"
    }, formatMoney(this.strikePriceFor(p))), formatMoney(this.activePriceFor(p)), isRecurring && m("small", null, "/", this.effectiveCycle(p) === 'yearly' ? this.t('shop.per_year_short') : this.t('shop.per_month_short'))), m("div", {
      className: "MpProduct-priceLabel"
    }, this.billingLabel(p)))), p.has_extended_license && m("div", {
      className: "MpProduct-cycleSwitch MpProduct-licenseSwitch",
      role: "radiogroup",
      "aria-label": this.t('shop.choose_license') || 'Escolha a licença'
    }, m("button", {
      type: "button",
      role: "radio",
      "aria-checked": this.selectedLicense === 'regular',
      className: this.selectedLicense === 'regular' ? 'is-active' : '',
      onclick: () => {
        this.selectedLicense = 'regular';
        m.redraw();
      }
    }, this.t('shop.license_regular') || 'Regular', m("small", null, formatMoney(p.price))), m("button", {
      type: "button",
      role: "radio",
      "aria-checked": this.selectedLicense === 'extended',
      className: this.selectedLicense === 'extended' ? 'is-active' : '',
      onclick: () => {
        this.selectedLicense = 'extended';
        m.redraw();
      }
    }, this.t('shop.license_extended') || 'Estendida', m("small", null, formatMoney(p.extended_price)))), p.has_dual_billing && !p.already_subscribed && m("div", {
      className: "MpProduct-cycleSwitch",
      role: "radiogroup",
      "aria-label": this.t('shop.choose_billing') || 'Escolha o ciclo'
    }, m("button", {
      type: "button",
      role: "radio",
      "aria-checked": this.selectedCycle === 'monthly',
      className: this.selectedCycle === 'monthly' ? 'is-active' : '',
      onclick: () => {
        this.selectedCycle = 'monthly';
        m.redraw();
      }
    }, this.t('shop.cycle_monthly') || 'Mensal', m("small", null, formatMoney(p.price), "/", this.t('shop.per_month_short') || 'mês')), m("button", {
      type: "button",
      role: "radio",
      "aria-checked": this.selectedCycle === 'yearly',
      className: this.selectedCycle === 'yearly' ? 'is-active' : '',
      onclick: () => {
        this.selectedCycle = 'yearly';
        m.redraw();
      }
    }, this.t('shop.cycle_yearly') || 'Anual', m("small", null, formatMoney(p.price_yearly), "/", this.t('shop.per_year_short') || 'ano'))), (p.type === 'physical' || isPool) && !outOfStock && (() => {
      const cap = isPool ? poolAvail : p.stock;
      return m("div", {
        className: "MpProduct-qty"
      }, m("button", {
        type: "button",
        disabled: this.qty <= 1,
        onclick: () => {
          this.qty = Math.max(1, this.qty - 1);
          m.redraw();
        }
      }, m("i", {
        className: "fas fa-minus"
      })), m("input", {
        type: "number",
        min: "1",
        max: cap,
        value: this.qty,
        oninput: e => {
          const v = Number(e.target.value) || 1;
          this.qty = Math.max(1, Math.min(cap, v));
          m.redraw();
        }
      }), m("button", {
        type: "button",
        disabled: this.qty >= cap,
        onclick: () => {
          this.qty = Math.min(cap, this.qty + 1);
          m.redraw();
        }
      }, m("i", {
        className: "fas fa-plus"
      })));
    })(), this.renderBuyButton(p, isRecurring, outOfStock), m(WishlistButton, {
      productId: p.id,
      className: "MpProduct-wishlistBtn"
    }), app.session.user && m("button", {
      type: "button",
      className: "MpBtn MpBtn--ghost MpBtn--small MpProduct-reportBtn",
      title: this.t('report.button') || 'Denunciar',
      onclick: () => this.reportProduct(p.id)
    }, m("i", {
      className: "fas fa-flag"
    }), " ", this.t('report.button')))), isComposer && m("div", {
      className: "MpProduct-composerHint"
    }, m("div", {
      className: "MpProduct-composerHint-text"
    }, m("i", {
      className: "fab fa-php"
    }), m("div", null, m("strong", null, this.t('product.composer_only') || 'Pacote Composer'), m("small", null, this.t('product.composer_hint') || 'Entregue via composer require - sem download ZIP direto.'))), composerRequire(p.package_name) ? m("button", {
      type: "button",
      className: "MpBtn MpBtn--ghost MpBtn--small",
      onclick: () => this.copy(composerRequire(p.package_name))
    }, m("code", null, "composer require ", p.package_name), m("i", {
      className: "fas fa-copy"
    })) : m("code", {
      className: "MpProduct-composerCmd"
    }, "composer require ", p.package_name)), m("div", {
      className: "MpProduct-cols"
    }, m("main", {
      className: "MpProduct-main"
    }, this.renderTabs(), this.tab === 'overview' && this.renderOverview(p), this.tab === 'readme' && this.renderReadme(p), this.tab === 'changelog' && this.renderChangelog(p), this.tab === 'reviews' && this.renderReviews()), m("aside", {
      className: "MpProduct-sidebar"
    }, this.renderInfoCard(p), this.renderRequirementsCard(p))), this.renderRelated());
  }
  renderBuyButton(p, isRecurring, outOfStock) {
    // Curso já adquirido → vai direto pro player, não pro detalhe do pedido.
    if (p.type === 'course' && p.already_owned && p.course?.id) {
      return m("a", {
        href: app.route('marketplace.course', {
          id: p.course.id
        }),
        config: m.route.link,
        className: "MpBtn MpBtn--primary MpProduct-buyBtn"
      }, m("i", {
        className: "fas fa-play"
      }), " ", this.t('product.go_to_course') || 'Ir para o curso');
    }

    // Usuário já tem esse produto (assinatura ativa ou one-time já comprado)?
    // O backend devolve o ID exato do pedido/assinatura — linkamos direto
    // pro detalhe pra o cliente acessar a license_key/download/cancelar
    // num clique (em vez de ter que procurar na lista).
    if (p.already_subscribed) {
      // Sub vigente mas com cancelamento agendado — mostra aviso e
      // botão de reativar. POST /my/subscriptions/{id}/reactivate
      // remove o cancel_at_period_end na Stripe e aqui no banco.
      if (p.subscription_cancelling && p.existing_subscription_id) {
        // Formata a data do fim do período em PT-BR — "25 de mai. de 2026"
        // pra ficar curto e claro. Se vier null, fallback pra mensagem genérica.
        const endsAt = p.subscription_period_end ? new Date(p.subscription_period_end).toLocaleDateString(undefined, {
          day: '2-digit',
          month: 'short',
          year: 'numeric'
        }) : null;
        const msg = endsAt ? this.t('product.subscription_cancelling_until', {
          date: endsAt
        }) || `Assinatura cancelada. Acesso até ${endsAt}.` : this.t('product.subscription_cancelling') || 'Assinatura cancelada - acesso até o fim do ciclo.';
        return m("div", {
          className: "MpProduct-cancelling"
        }, m("button", {
          type: "button",
          className: "MpBtn MpBtn--primary MpBtn--small MpProduct-cancelling-btn",
          disabled: this.reactivating,
          onclick: () => this.reactivateSubscription(p)
        }, m("i", {
          className: this.reactivating ? 'fas fa-circle-notch fa-spin' : 'fas fa-redo'
        }), ' ', this.t('product.reactivate_subscription') || 'Reativar assinatura'), m("small", {
          className: "MpProduct-cancelling-msg"
        }, msg));
      }

      // Texto diferenciado: assinatura recorrente VS serviço (sem cobrança
      // dual via Stripe Subs). Tooltip mantém a frase completa.
      const subLabel = p.type === 'service' ? this.t('product.service_active') || 'Serviço ativo' : this.t('product.subscription_active') || 'Assinatura ativa';
      const href = p.existing_subscription_id ? app.route('marketplace.subscriptions') + '#sub-' + p.existing_subscription_id : app.route('marketplace.subscriptions');
      return m("a", {
        href: href,
        config: m.route.link,
        className: "MpBtn MpProduct-buyBtn MpProduct-ownedBtn",
        title: subLabel
      }, m("i", {
        className: "fas fa-check-circle"
      }), subLabel);
    }
    // "Já possui" só faz sentido para produtos NÃO-físicos: digital/service
    // são entregues uma vez e ponto. Físico é re-comprável (pode pedir
    // unidades adicionais), então sempre mostra o botão normal de compra.
    // Pool unique_per_buyer também é re-comprável por design — cada
    // compra entrega um arquivo NOVO do pool, então mostramos sempre o
    // botão padrão "Adicionar" mesmo se o user já tem ingresso(s).
    if (p.already_owned && p.type !== 'physical' && !p.unique_per_buyer) {
      // Service one-time (sem subscription) também mostra "Serviço ativo"
      // — é um acesso único concedido e o cliente já tem.
      const ownedLabel = p.type === 'service' ? this.t('product.service_active') || 'Serviço ativo' : this.t('product.already_owned') || 'Já possui';
      const href = p.existing_order_id ? app.route('marketplace.order.detail', {
        id: p.existing_order_id
      }) : app.route('marketplace.orders');
      return m("a", {
        href: href,
        config: m.route.link,
        className: "MpBtn MpProduct-buyBtn MpProduct-ownedBtn",
        title: ownedLabel
      }, m("i", {
        className: "fas fa-check-circle"
      }), ownedLabel);
    }
    const added = this.justAddedToCart;

    // Depois da janela de confirmação (added), se o item segue no carrinho
    // o botão vira link pra /cart em vez de permitir adicionar de novo —
    // sem isso é fácil duplicar o mesmo item clicando duas vezes.
    if (!added && this.isInCart(p.id)) {
      return m("a", {
        href: app.route('marketplace.cart'),
        config: m.route.link,
        className: "MpBtn MpProduct-buyBtn MpProduct-ownedBtn",
        title: this.t('shop.already_in_cart') || 'Já no carrinho'
      }, m("i", {
        className: "fas fa-check-circle"
      }), this.t('shop.already_in_cart') || 'Já no carrinho');
    }
    return m(Button, {
      className: 'MpBtn MpProduct-buyBtn ' + (added ? 'MpBtn--success MpProduct-buyBtn--added' : 'MpBtn--primary'),
      icon: added ? 'fas fa-check' : isRecurring ? 'fas fa-sync-alt' : 'fas fa-cart-plus',
      disabled: outOfStock || added,
      onclick: () => this.addToCart(p.id)
    }, outOfStock ? this.t('shop.out_of_stock') : added ? this.t('shop.added_to_cart') || 'Adicionado ao carrinho' : isRecurring ? this.t('product.subscribe') || 'Assinar agora' : this.t('product.add_to_cart'));
  }
  renderTabs() {
    const tabs = [['overview', this.t('product.tab_overview') || 'Overview', true]];
    if (this.product.has_readme) {
      tabs.push(['readme', this.t('product.tab_readme') || 'README', true]);
    }
    if (this.product.changelog || this.product.versions_total) {
      tabs.push(['changelog', this.t('product.tab_changelog') || 'Changelog', true]);
    }
    tabs.push(['reviews', `${this.t('product.tab_reviews') || 'Avaliações'} (${this.reviewMeta?.total ?? 0})`, true]);
    return m("nav", {
      className: "MpProduct-tabs"
    }, tabs.map(_ref => {
      let [key, label] = _ref;
      return m("button", {
        type: "button",
        key: key,
        className: `MpProduct-tab${this.tab === key ? ' is-active' : ''}`,
        onclick: () => {
          this.tab = key;
          m.redraw();
        }
      }, label);
    }));
  }
  renderOverview(p) {
    return m("div", {
      className: "MpProduct-section"
    }, m("h2", null, this.t('product.tab_overview') || 'Overview'), p.description ? m("div", {
      className: "MpProduct-prose"
    }, p.description.split(/\n{2,}/).map((para, i) => m("p", {
      key: i
    }, para))) : m("p", {
      className: "MpProduct-prose helpText"
    }, this.t('product.no_description') || 'Sem descrição disponível.'), p.features && m("div", {
      className: "MpProduct-section"
    }, m("h2", null, this.t('product.features') || 'Recursos'), m("pre", {
      className: "MpProduct-features"
    }, p.features)), this.renderScreenshots(p), this.renderCourseContents(p));
  }
  renderCourseContents(p) {
    const course = p.course;
    if (!course || !Array.isArray(course.lessons) || course.lessons.length === 0) return null;
    const owned = !!p.already_owned;
    return m("div", {
      className: "MpProduct-section MpProduct-course"
    }, m("h2", null, m("i", {
      className: "fas fa-graduation-cap"
    }), " ", this.t('product.course_contents') || 'Conteúdo do curso'), course.overview && m("p", {
      className: "MpProduct-prose"
    }, course.overview), m("ul", {
      className: "MpProduct-courseLessons"
    }, course.lessons.map(l => m("li", {
      key: l.id,
      className: 'MpProduct-courseLesson' + (l.is_preview ? ' is-preview' : '')
    }, m("i", {
      className: 'fas fa-' + (l.is_preview || owned ? 'play-circle' : 'lock')
    }), m("span", null, l.title), l.is_preview && m("span", {
      className: "MpProduct-previewTag"
    }, this.t('product.preview') || 'Prévia')))), owned && m("a", {
      href: app.route('marketplace.course', {
        id: course.id
      }),
      config: m.route.link,
      className: "MpBtn MpBtn--primary"
    }, m("i", {
      className: "fas fa-play"
    }), " ", this.t('product.go_to_course') || 'Ir para o curso'));
  }
  renderScreenshots(p) {
    const shots = Array.isArray(p.screenshots) ? p.screenshots : [];
    // Pool unique_per_buyer (ingressos, códigos): screenshots não fazem
    // sentido — o "produto" é um arquivo único entregue após a compra.
    // Só mostra a seção se houver screenshots de fato uploadadas.
    if (p.unique_per_buyer && shots.length === 0) return null;
    const MAX_THUMBS = 6;
    const visible = shots.slice(0, MAX_THUMBS);
    const hidden = Math.max(0, shots.length - MAX_THUMBS);
    return m("div", {
      className: "MpProduct-section"
    }, m("h2", null, this.t('product.screenshots') || 'Screenshots'), shots.length === 0 ? m("div", {
      className: "MpProduct-screensEmpty"
    }, m("i", {
      className: "fas fa-image"
    }), m("span", null, this.t('product.no_screenshots') || 'Sem screenshots')) : m("div", {
      className: "MpProduct-gallery"
    }, visible.map((s, i) => {
      const isLast = i === visible.length - 1 && hidden > 0;
      return m("button", {
        type: "button",
        key: s,
        className: "MpProduct-gallery-item",
        onclick: e => {
          e.preventDefault();
          this.openLightbox(i);
        }
      }, m("img", {
        src: `/assets/marketplace/${s}`,
        alt: "",
        loading: "lazy"
      }), isLast && m("span", {
        className: "MpProduct-gallery-more"
      }, "+", hidden));
    })), this.lightboxIndex !== null && this.renderLightbox(shots));
  }
  openLightbox(index) {
    this.lightboxIndex = index;
    document.body.style.overflow = 'hidden';
    m.redraw();
  }
  closeLightbox() {
    this.lightboxIndex = null;
    document.body.style.overflow = '';
    m.redraw();
  }
  moveLightbox(delta, total) {
    if (this.lightboxIndex === null) return;
    this.lightboxIndex = (this.lightboxIndex + delta + total) % total;
    m.redraw();
  }
  renderLightbox(shots) {
    const idx = this.lightboxIndex;
    const src = `/assets/marketplace/${shots[idx]}`;
    return m("div", {
      className: "MpLightbox",
      onclick: () => this.closeLightbox(),
      onkeydown: e => {
        if (e.key === 'Escape') this.closeLightbox();
        if (e.key === 'ArrowRight') this.moveLightbox(1, shots.length);
        if (e.key === 'ArrowLeft') this.moveLightbox(-1, shots.length);
      },
      tabindex: "0",
      oncreate: vnode => vnode.dom.focus()
    }, m("button", {
      type: "button",
      className: "MpLightbox-close",
      onclick: e => {
        e.stopPropagation();
        this.closeLightbox();
      },
      title: this.t('product.close')
    }, m("i", {
      className: "fas fa-times"
    })), shots.length > 1 && m("button", {
      type: "button",
      className: "MpLightbox-nav MpLightbox-nav--prev",
      onclick: e => {
        e.stopPropagation();
        this.moveLightbox(-1, shots.length);
      }
    }, m("i", {
      className: "fas fa-chevron-left"
    })), m("img", {
      className: "MpLightbox-img",
      src: src,
      alt: "",
      onclick: e => e.stopPropagation()
    }), shots.length > 1 && m("button", {
      type: "button",
      className: "MpLightbox-nav MpLightbox-nav--next",
      onclick: e => {
        e.stopPropagation();
        this.moveLightbox(1, shots.length);
      }
    }, m("i", {
      className: "fas fa-chevron-right"
    })), m("div", {
      className: "MpLightbox-counter"
    }, idx + 1, " / ", shots.length));
  }
  coursePreviewUrl = (() => undefined)();
  coursePlaying = false;
  embedUrl(url) {
    const yt = url.match(/(?:v=|youtu\.be\/|embed\/)([A-Za-z0-9_-]{6,})/);
    if (yt) return 'https://www.youtube.com/embed/' + yt[1] + '?rel=0';
    const vm = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
    if (vm) return 'https://player.vimeo.com/video/' + vm[1];
    return url;
  }
  formatDuration(seconds) {
    if (!seconds || seconds <= 0) return '';
    const h = Math.floor(seconds / 3600);
    const mnt = Math.round(seconds % 3600 / 60);
    if (h > 0) return h + 'h' + (mnt > 0 ? ' ' + mnt + 'm' : '');
    return mnt + 'm';
  }
  renderCourseView(p) {
    const course = p.course || {};
    const lessons = Array.isArray(course.lessons) ? course.lessons : [];
    const isFree = p.price <= 0;
    const owned = !!p.already_owned;
    const hasPreview = lessons.some(l => l.preview_video_url);
    if (this.coursePreviewUrl === undefined) {
      const firstPreview = lessons.find(l => l.preview_video_url);
      this.coursePreviewUrl = firstPreview ? firstPreview.preview_video_url : course.intro_video_url || null;
    }
    const duration = this.formatDuration(course.duration_seconds || 0);
    return m("div", {
      className: "MpProduct MpCoursePage"
    }, MpNavHelper(), m("a", {
      href: app.route('marketplace.courses_store'),
      config: m.route.link,
      className: "MpProduct-back"
    }, m("i", {
      className: "fas fa-chevron-left"
    }), " ", this.t('product.back_to_courses') || 'Voltar aos cursos'), m("div", {
      className: "MpCoursePage-grid"
    }, this.renderCoursePlayer(p, course), m("div", {
      className: "MpCoursePage-area MpCoursePage-area--head"
    }, m("h1", null, p.name), m("div", {
      className: "MpCoursePage-meta"
    }, p.rating > 0 && m("span", {
      className: "MpProduct-rating"
    }, this.renderStars(p.rating), m("strong", null, Number(p.rating).toFixed(1)), p.reviews_count ? m("small", null, "(", p.reviews_count, ")") : null), m("span", {
      className: "MpCoursePage-metaItem"
    }, m("i", {
      className: "fas fa-play-circle"
    }), " ", this.t('product.lessons_n', {
      count: course.lesson_count || lessons.length
    })), duration && m("span", {
      className: "MpCoursePage-metaItem"
    }, m("i", {
      className: "fas fa-clock"
    }), " ", duration))), m("aside", {
      className: "MpCoursePage-area MpCoursePage-area--buy"
    }, m("div", {
      className: "MpCoursePage-buyCard"
    }, m("div", {
      className: "MpCoursePage-buyTitle"
    }, this.t('product.full_course') || 'Curso completo'), m("div", {
      className: "MpCoursePage-buyPrice"
    }, isFree ? this.t('shop.free') || 'Grátis' : formatMoney(this.activePriceFor(p))), m("div", {
      className: "MpCoursePage-buyAction"
    }, this.renderBuyButton(p, false, false)), m("ul", {
      className: "MpCoursePage-includes"
    }, m("li", null, m("i", {
      className: "fas fa-play-circle"
    }), " ", this.t('product.lessons_n', {
      count: course.lesson_count || lessons.length
    })), duration && m("li", null, m("i", {
      className: "fas fa-clock"
    }), " ", this.t('product.includes_duration', {
      duration
    }) || duration), hasPreview && m("li", null, m("i", {
      className: "fas fa-eye"
    }), " ", this.t('product.includes_preview') || 'Prévia disponível'), m("li", null, m("i", {
      className: "fas fa-infinity"
    }), " ", this.t('product.includes_lifetime') || 'Acesso vitalício')), m("p", {
      className: "MpCoursePage-guarantee"
    }, m("i", {
      className: "fas fa-shield-alt"
    }), " ", this.t('product.money_back') || 'Garantia de reembolso de 7 dias'))), m("section", {
      className: "MpProduct-section MpCoursePage-area MpCoursePage-area--about"
    }, m("h2", null, this.t('product.tab_overview') || 'Sobre o curso'), course.overview_html ? m("div", {
      className: "MpProduct-prose MarketplaceReadme"
    }, m.trust(course.overview_html)) : p.description ? m("div", {
      className: "MpProduct-prose"
    }, p.description.split(/\n{2,}/).map((para, i) => m("p", {
      key: i
    }, para))) : m("p", {
      className: "MpProduct-prose helpText"
    }, this.t('product.no_description') || 'Sem descrição.')), m("section", {
      className: "MpProduct-section MpCoursePage-area MpCoursePage-area--curric"
    }, m("h2", null, this.t('product.course_curriculum') || 'Conteúdo do curso'), m("p", {
      className: "helpText"
    }, this.t('product.course_curriculum_hint', {
      count: course.lesson_count || lessons.length
    })), m("ol", {
      className: "MpCoursePage-curriculum"
    }, lessons.map((l, i) => {
      const playable = !!l.preview_video_url;
      return m("li", {
        key: l.id,
        className: 'MpCoursePage-lesson' + (playable ? ' is-playable' : ''),
        onclick: playable ? () => {
          this.coursePreviewUrl = l.preview_video_url;
          this.coursePlaying = true;
          window.scrollTo({
            top: 0,
            behavior: 'smooth'
          });
        } : undefined
      }, m("span", {
        className: "MpCoursePage-lessonNum"
      }, i + 1), m("span", {
        className: "MpCoursePage-lessonTitle"
      }, l.title), l.duration_seconds > 0 && m("span", {
        className: "MpCoursePage-lessonDur"
      }, this.formatDuration(l.duration_seconds)), l.is_preview ? m("span", {
        className: "MpChip MpChip--sub"
      }, this.t('product.preview') || 'Prévia') : m("i", {
        className: 'fas ' + (owned ? 'fa-play-circle' : 'fa-lock') + ' MpCoursePage-lessonIcon'
      }));
    }))), m("div", {
      className: "MpCoursePage-area MpCoursePage-area--reviews"
    }, this.renderReviews())));
  }
  renderCoursePlayer(p, course) {
    const cover = p.image ? mpImage(p.image, 'lg') : course.image ? {
      src: course.image
    } : null;
    const hasVideo = !!this.coursePreviewUrl;
    return m("div", {
      className: "MpCoursePage-area MpCoursePage-area--player"
    }, m("div", {
      className: "MpCoursePage-player"
    }, this.coursePlaying && hasVideo ? m("iframe", {
      src: this.embedUrl(this.coursePreviewUrl),
      allow: "autoplay; fullscreen; picture-in-picture",
      allowfullscreen: true,
      frameborder: "0"
    }) : cover ? m("div", {
      className: "MpCoursePage-cover"
    }, m("img", Object.assign({}, cover, {
      alt: p.name
    })), hasVideo && m("button", {
      type: "button",
      className: "MpCoursePage-coverPlay",
      onclick: () => this.coursePlaying = true,
      "aria-label": this.t('product.play')
    }, m("i", {
      className: "fas fa-play"
    }))) : hasVideo ? m("iframe", {
      src: this.embedUrl(this.coursePreviewUrl),
      allow: "autoplay; fullscreen; picture-in-picture",
      allowfullscreen: true,
      frameborder: "0"
    }) : m("div", {
      className: "MpCoursePage-playerEmpty"
    }, m("i", {
      className: "fas fa-graduation-cap"
    }))));
  }
  loadChangelogVersions(offset) {
    this.clLoading = true;
    this.clLoaded = true;
    m.redraw();
    api('GET', `/marketplace/products/${this.product.id}/versions-public?offset=${offset}&limit=${this.clLimit}`).then(r => {
      this.clVersions = r?.data ?? [];
      this.clTotal = r?.total ?? 0;
      this.clOffset = r?.offset ?? offset;
    }).catch(() => {
      this.clVersions = [];
    }).finally(() => {
      this.clLoading = false;
      m.redraw();
    });
  }
  renderChangelog(p) {
    const hasHistory = (p.versions_total ?? 0) > 0;
    if (hasHistory) {
      if (!this.clLoaded) this.loadChangelogVersions(0);
      const page = Math.floor(this.clOffset / this.clLimit) + 1;
      const pages = Math.max(1, Math.ceil((this.clTotal || 0) / this.clLimit));
      return m("div", {
        className: "MpProduct-section"
      }, m("h2", null, this.t('product.tab_changelog') || 'Changelog'), this.clLoading ? m("p", {
        className: "MpProduct-prose helpText"
      }, this.t('product.loading') || 'Carregando...') : m('[', null, m("ol", {
        className: "MpProduct-releases"
      }, this.clVersions.map(v => m("li", {
        key: v.version,
        className: "MpProduct-release"
      }, m("div", {
        className: "MpProduct-releaseHead"
      }, m("code", {
        className: "MpProduct-releaseVer"
      }, v.version), v.source === 'github' && m("span", {
        className: "MpChip MpChip--ot"
      }, m("i", {
        className: "fab fa-github"
      })), v.released_at && m("span", {
        className: "MpProduct-releaseDate"
      }, this.formatReleaseDate(v.released_at))), v.changelog_html ? m("div", {
        className: "MpProduct-prose MarketplaceReadme"
      }, m.trust(v.changelog_html)) : m("p", {
        className: "MpProduct-prose helpText"
      }, this.t('product.no_changelog') || 'Sem notas.')))), pages > 1 && m("div", {
        className: "MpProduct-releasePager"
      }, m("button", {
        type: "button",
        className: "MpBtn MpBtn--outline MpBtn--small",
        disabled: this.clOffset <= 0 || this.clLoading,
        onclick: () => this.loadChangelogVersions(Math.max(0, this.clOffset - this.clLimit))
      }, m("i", {
        className: "fas fa-chevron-left"
      }), " ", this.t('product.prev') || 'Anterior'), m("span", {
        className: "MpProduct-releasePage"
      }, page, " / ", pages), m("button", {
        type: "button",
        className: "MpBtn MpBtn--outline MpBtn--small",
        disabled: this.clOffset + this.clLimit >= this.clTotal || this.clLoading,
        onclick: () => this.loadChangelogVersions(this.clOffset + this.clLimit)
      }, this.t('product.next') || 'Próxima', " ", m("i", {
        className: "fas fa-chevron-right"
      })))));
    }
    return m("div", {
      className: "MpProduct-section"
    }, m("h2", null, this.t('product.tab_changelog') || 'Changelog'), p.changelog_html ? m("div", {
      className: "MpProduct-prose MarketplaceReadme"
    }, m.trust(p.changelog_html)) : p.changelog ? m("pre", {
      className: "MpProduct-features"
    }, p.changelog) : m("p", {
      className: "MpProduct-prose helpText"
    }, this.t('product.no_changelog') || 'Sem histórico.'));
  }
  formatReleaseDate(iso) {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleDateString(undefined, {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  }
  renderReadme(p) {
    if (!this.readmeLoaded && !this.readmeLoading) {
      this.readmeLoading = true;
      api('GET', `/marketplace/products/${p.id}/readme`).then(r => {
        this.readmeHtml = r?.data?.html ?? null;
      }).catch(() => {
        this.readmeHtml = null;
      }).finally(() => {
        this.readmeLoading = false;
        this.readmeLoaded = true;
        m.redraw();
      });
    }
    return m("div", {
      className: "MpProduct-section"
    }, m("h2", null, this.t('product.tab_readme') || 'README'), this.readmeLoading ? m("p", {
      className: "MpProduct-prose helpText"
    }, this.t('product.loading') || 'Carregando...') : this.readmeHtml ? m("div", {
      className: "MarketplaceReadme"
    }, m.trust(this.readmeHtml)) : m("p", {
      className: "MpProduct-prose helpText"
    }, this.t('product.readme_empty') || 'Sem README.'));
  }
  renderReviews() {
    return m("div", {
      className: "MpProduct-section"
    }, m("h2", null, this.t('product.reviews_title') || 'Avaliações', m("small", null, " (", this.reviewMeta.total, ")")), app.session.user && this.renderReviewForm(), this.reviews.length === 0 ? m("p", {
      className: "MpProduct-prose helpText"
    }, this.t('product.no_reviews') || 'Ainda sem avaliações.') : m("ul", {
      className: "MpProduct-reviews"
    }, this.reviews.map(r => m("li", {
      key: r.id,
      className: "MpProduct-review"
    }, m("div", {
      className: "MpProduct-reviewAvatar"
    }, r.avatar_url ? m("img", {
      src: r.avatar_url,
      alt: "",
      loading: "lazy"
    }) : r.user_name.charAt(0).toUpperCase()), m("div", {
      className: "MpProduct-reviewBody"
    }, m("div", {
      className: "MpProduct-reviewHead"
    }, m("strong", null, r.user_name), this.renderStars(r.rating), m("span", null, this.formatDate(r.created_at))), r.comment && m("p", null, r.comment))))));
  }
  loadRelated(id) {
    this.relatedLoaded = false;
    api('GET', `/marketplace/products/${id}/related`).then(r => {
      this.related = Array.isArray(r?.data) ? r.data : [];
    }).catch(() => {
      this.related = [];
    }).finally(() => {
      this.relatedLoaded = true;
      m.redraw();
    });
  }

  /**
   * Seção "Você também pode gostar". Enquanto a recomendação não volta, a
   * seção fica reservada com o mesmo card em skeleton: ela é buscada DEPOIS do
   * produto, e sem isso nascia pronta no fim do carregamento, empurrando a
   * página. Sem recomendação nenhuma, some (não há seção no conteúdo real).
   */
  renderRelated() {
    if (!this.relatedLoaded) {
      return m("section", {
        className: "MpProduct-related MpSkel-mirror",
        "aria-hidden": "true"
      }, m("div", {
        className: "MpSkel MpSkel--title MpSkel--w-30"
      }), m("div", {
        className: "MpRelated-grid"
      }, [0, 1, 2, 3].map(i => m("div", {
        className: "MpRelated-card",
        key: String(i)
      }, m("div", {
        className: "MpRelated-thumb"
      }, m("div", {
        className: "MpSkel MpSkel--fill"
      })), m("div", {
        className: "MpRelated-body"
      }, m("div", {
        className: "MpSkel MpSkel--line MpSkel--w-70"
      }), m("div", {
        className: "MpSkel MpSkel--line MpSkel--w-40"
      }), m("div", {
        className: "MpRelated-foot"
      }, m("div", {
        className: "MpSkel MpSkel--line MpSkel--tx-sm"
      })))))));
    }
    if (this.related.length === 0) return null;
    return m("section", {
      className: "MpProduct-related"
    }, m("h2", null, this.t('product.related_title') || 'Você também pode gostar'), m("div", {
      className: "MpRelated-grid"
    }, this.related.map(r => this.renderRelatedCard(r))));
  }
  renderRelatedCard(r) {
    const href = this.canonicalUrl(r) || app.route('marketplace.product', {
      id: r.id
    });
    const color = this.colorFor(r.name);
    const price = r.on_sale && r.sale_price != null ? r.sale_price : r.price;
    return m("a", {
      key: r.id,
      href: href,
      config: m.route.link,
      className: "MpRelated-card"
    }, m("div", {
      className: "MpRelated-thumb",
      style: {
        '--mp-icon-bg': color + '1f',
        '--mp-icon-fg': color
      }
    }, r.image ? m("img", Object.assign({}, mpImage(r.image), {
      alt: r.name,
      loading: "lazy"
    })) : m("i", {
      className: this.iconFor(r)
    })), m("div", {
      className: "MpRelated-body"
    }, m("div", {
      className: "MpRelated-name",
      title: r.name
    }, r.name), r.category && m("div", {
      className: "MpRelated-cat"
    }, r.category.name), m("div", {
      className: "MpRelated-foot"
    }, m("span", {
      className: "MpRelated-price"
    }, r.is_open_source ? this.t('shop.free') || 'Grátis' : formatMoney(price)), r.rating > 0 && m("span", {
      className: "MpRelated-rating"
    }, m("i", {
      className: "fas fa-star"
    }), " ", Number(r.rating).toFixed(1)))));
  }
  renderInfoCard(p) {
    const items = [];
    if (p.package_name) {
      items.push({
        icon: 'fab fa-php',
        label: 'Package',
        value: m("code", null, p.package_name)
      });
    }
    if (p.version) {
      items.push({
        icon: 'fas fa-code-branch',
        label: this.t('product.version'),
        value: m("strong", null, p.version)
      });
    }
    if (p.version_updated_at) {
      items.push({
        icon: 'fas fa-calendar-alt',
        label: this.t('product.version_updated_at'),
        value: this.formatDate(p.version_updated_at)
      });
    }
    items.push({
      icon: 'fas fa-receipt',
      label: this.t('discover.billing') || 'Cobrança',
      value: this.billingLabel(p)
    });
    if (p.reviews_count > 0 || p.rating > 0) {
      items.push({
        icon: 'fas fa-star',
        label: this.t('product.rating') || 'Avaliação',
        value: m("span", {
          className: "MpProduct-infoRating"
        }, m("strong", null, Number(p.rating).toFixed(1)), " (", p.reviews_count, ")")
      });
    }
    if (p.type === 'physical') {
      items.push({
        icon: 'fas fa-boxes',
        label: this.t('product.stock') || 'Estoque',
        value: p.stock > 0 ? `${p.stock} ${this.t('product.units') || 'unidades'}` : this.t('shop.out_of_stock')
      });
    }
    return m("div", {
      className: "MpProduct-card"
    }, m("h3", null, this.t('product.info') || 'Informações'), m("div", {
      className: "MpProduct-infoList"
    }, items.map((it, i) => m("div", {
      className: "MpProduct-infoRow",
      key: i
    }, m("div", {
      className: "MpProduct-infoIcon"
    }, m("i", {
      className: it.icon
    })), m("div", {
      className: "MpProduct-infoContent"
    }, m("span", {
      className: "MpProduct-infoLabel"
    }, it.label), m("span", {
      className: "MpProduct-infoValue"
    }, it.value))))));
  }
  renderRequirementsCard(p) {
    const reqs = [];
    if (p.requires_flarum) reqs.push(`Flarum ${p.requires_flarum}`);
    if (p.requires_php) reqs.push(`PHP ${p.requires_php}`);
    if (reqs.length === 0 && p.package_name) {
      reqs.push('Flarum 2.0+', 'PHP 8.1+');
    }
    if (reqs.length === 0) return null;
    return m("div", {
      className: "MpProduct-card"
    }, m("h3", null, this.t('product.requirements') || 'Requisitos'), m("ul", {
      className: "MpProduct-reqList"
    }, reqs.map((r, i) => m("li", {
      key: i
    }, m("i", {
      className: "fas fa-check-circle"
    }), m("span", null, r)))));
  }
  renderReviewForm() {
    return m("form", {
      className: "MpProduct-reviewForm",
      onsubmit: e => {
        e.preventDefault();
        this.submitReview();
      }
    }, m("h3", null, this.t('product.write_review')), m("label", null, this.t('product.your_rating')), m("div", {
      className: "MpProduct-reviewFormStars"
    }, [1, 2, 3, 4, 5].map(n => m("i", {
      key: n,
      className: `fas fa-star ${this.myRating >= n ? 'active' : ''}`,
      onclick: () => {
        this.myRating = n;
        m.redraw();
      }
    }))), m("textarea", {
      rows: "3",
      placeholder: this.t('product.comment_placeholder'),
      value: this.myComment,
      oninput: e => {
        this.myComment = e.target.value;
        m.redraw();
      }
    }), m(Button, {
      type: "submit",
      className: "MpBtn MpBtn--primary",
      loading: this.submittingReview
    }, this.t('product.submit_review')));
  }
  async submitReview() {
    if (this.submittingReview) return;
    this.submittingReview = true;
    try {
      await api('POST', `/marketplace/products/${this.product.id}/reviews`, {
        rating: this.myRating,
        comment: this.myComment
      });
      this.myComment = '';
      app.alerts.show({
        type: 'success'
      }, this.t('product.review_saved'));
      await this.load();
    } catch (e) {
      const err = e?.response?.error;
      const key = err === 'purchase_required' ? 'purchase_required' : 'review_error';
      app.alerts.show({
        type: 'error'
      }, this.t('product.' + key));
    } finally {
      this.submittingReview = false;
      m.redraw();
    }
  }
  renderStars(rating) {
    return m("span", {
      className: "MpProduct-stars"
    }, [1, 2, 3, 4, 5].map(n => m("i", {
      key: n,
      className: `fas fa-star ${rating >= n - 0.25 ? 'active' : ''}`
    })));
  }
  async copy(text) {
    const ok = await copyCommand(text);
    app.alerts.show({
      type: ok ? 'success' : 'error'
    }, ok ? this.t('packages.copied') || 'Copiado' : this.t('packages.copy_refused') || 'Nada foi copiado: o comando não parece seguro.');
  }
}
flarum.reg.add('ramon-marketplace', 'forum/components/ProductPage', ProductPage);